import React from 'react';
import './Main_account_creation.css';

import Tracker from '../../../layout_components/Tracker/Tracker';
import Account_step_1 from '../Account_step_1/Account_step_1.js';
import Account_step_2 from '../Account_step_2/Account_step_2.js';
import Account_step_3 from '../Account_step_3/Account_step_3.js';
import Account_step_4 from '../Account_step_4/Account_step_4.js';
import Account_step_5 from '../Account_step_5/Account_step_5.js';
import MasterButton from '../../../general_components/MasterButton/MasterButton.js';

import GGApi from '../../../../utils/GGApi.js';
import validations from '../../../../utils/validations.js';
import Header_for_multistep from '../../../layout_components/Header_for_multistep/Header_for_multistep';
import Overlay_popup from '../../../general_components/Overlay_popup/Overlay_popup.js';

import cover_picture from '../../../../images/illustrations/personalized_cash_gift_app.png';
import { Link } from 'react-router-dom';

import { logEvent } from '../../../../utils/analytics';
import { logSignupStepCompleted, syncExperimentUserProperties } from '../../../../utils/flowAnalytics';
import { EXPERIMENTS, VARIANTS, getVariant } from '../../../../utils/experiments';
import { getSignupValueProps } from '../signup_value_props.js';
import { Helmet } from 'react-helmet';

/** Screens in the order they are shown, per variant. */
const SCREEN_ORDER = {
    // Opening on "what is your legal name?" with no context is where the flow leaks.
    [VARIANTS.CONTROL]: ['name', 'contact', 'verify', 'credentials'],
    [VARIANTS.TREATMENT]: ['contact', 'verify', 'name', 'credentials'],
};

const SCREEN_TAGS = {
    name: 'Your name',
    contact: 'Contact',
    verify: 'Verify',
    credentials: 'Username & Password',
};

class Main_account_creation extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            //form details
            currentStep: 1,
            buttonLinks: false,
            open_overlay: false,

            //submission details
            first_name: '',
            last_name: '',
            user_email: '',
            user_phone_number: '',
            phone_verification: '',
            username: '',
            user_password: '',
            user_password_verification: '',

            //supporting states for masking purposes
            phone_support:'',

            // uniqueness of inputs
            email_unique: '',
            phone_unique: '',
            username_unique: '',

            field_errors: {},
            email_taken: false,

            // navigation
            next_page: '/greatest_gift_intro',
            next_page_params: '',

            // value copy shown on the first screen
            value_props: getSignupValueProps({}),

            /**
             * Contact-first experiment. Resolved in the constructor so the first
             * render already uses the right ordering, and sticky so a refresh
             * mid-signup does not move the user to a different screen.
             */
            contact_first_variant: getVariant(EXPERIMENTS.SIGNUP_CONTACT_FIRST),
        }

        this.handleButtonClick = this.handleButtonClick.bind(this);
        this.handleBackButton = this.handleBackButton.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleChangeEmail = this.handleChangeEmail.bind(this);
        this.checkUnique = this.checkUnique.bind(this)
        this.handlePhoneChange = this.handlePhoneChange.bind(this);
        this.overlayButton1Handler = this.overlayButton1Handler.bind(this);
    }

    /* ---------- step ordering ---------- */

    isContactFirst() {
        return this.state.contact_first_variant === VARIANTS.TREATMENT;
    }

    getScreenOrder() {
        return this.isContactFirst() ? SCREEN_ORDER[VARIANTS.TREATMENT] : SCREEN_ORDER[VARIANTS.CONTROL];
    }

    getScreenForStep(step) {
        return this.getScreenOrder()[step - 1];
    }

    getStepForScreen(screen) {
        return this.getScreenOrder().indexOf(screen) + 1;
    }

    getTrackerTags() {
        return this.getScreenOrder().map(screen => SCREEN_TAGS[screen]);
    }

    getFlowVariant() {
        return this.isContactFirst() ? 'contact_first' : 'name_first';
    }

    /**
     * Derived rather than stored. The previous version set the label on the way
     * forward and restored it on the way back, which desyncs as soon as the
     * steps move.
     */
    getStepButtonText() {
        const screen = this.getScreenForStep(this.state.currentStep);
        if (screen === 'verify') return 'Verify Number';
        if (screen === 'credentials') return 'Create Account';
        return 'Next';
    }

    /** Order-agnostic step event so one funnel reads correctly for both orderings. */
    logStepComplete(step, screen) {
        logSignupStepCompleted({
            step_index: step,
            step_name: SCREEN_TAGS[screen],
            flow_variant: this.getFlowVariant(),
        });
    }

    setFieldError(field, message) {
        this.setState(prev => ({ field_errors: { ...prev.field_errors, [field]: message } }));
    }

    clearFieldError(field) {
        this.setState(prev => {
            if (!prev.field_errors[field]) return null;
            const field_errors = { ...prev.field_errors };
            delete field_errors[field];
            return { field_errors };
        });
    }

    /* ---------- per-screen submits ---------- */

    submitName(step) {
        if (!this.state.first_name || !this.state.last_name) {
            this.setState({ field_errors: {
                ...this.state.field_errors,
                ...(!this.state.first_name ? { first_name: 'Please enter your first name.' } : {}),
                ...(!this.state.last_name ? { last_name: 'Please enter your last name.' } : {}),
            }});
            return;
        }
        logEvent('signup: submit name', { source: 'signup_flow', step });
        this.logStepComplete(step, 'name');
        this.setState({ currentStep: step + 1 });
    }

    submitContact(step) {
        const email = this.state.user_email.toLowerCase();
        const phone = this.state.user_phone_number;

        const errors = {};
        if (!validations.validateEmail(email)) {
            errors.user_email = 'Please enter a valid email address.';
        }
        if (!validations.validatePhone(phone)) {
            errors.user_phone_number = 'Please enter a valid 10-digit US phone number (XXX-XXX-XXXX).';
        }
        if (Object.keys(errors).length) {
            this.setState({ field_errors: errors });
            return;
        }

        // re-check the current values
        Promise.all([
            GGApi.uniqueEmail(email),
            GGApi.uniquePhone(phone),
        ]).then(([emailResult, phoneResult]) => {
            if (emailResult.status !== 'ok' || phoneResult.status !== 'ok') {
                this.setState({ field_errors: {
                    user_email: 'Something went wrong. Please try again shortly.',
                }});
                return;
            }

            const uniquenessErrors = {};
            if (!emailResult.unique) {
                uniquenessErrors.user_email = 'This email already has a Greatest Gift account.';
            }
            if (!phoneResult.unique) {
                uniquenessErrors.user_phone_number = 'This phone number already has a Greatest Gift account.';
            }

            this.setState({
                email_unique: emailResult.unique,
                phone_unique: phoneResult.unique,
                email_taken: !emailResult.unique,
                field_errors: uniquenessErrors,
            });

            if (Object.keys(uniquenessErrors).length) {
                return;
            }

            GGApi.verify_phone_number(phone);
            logEvent('signup: submit contact info', { source: 'signup_flow', step });
            this.logStepComplete(step, 'contact');
            this.setState({ currentStep: step + 1 });
        });
    }

    submitVerify(step) {
        GGApi.verify_check_number(this.state.user_phone_number, this.state.phone_verification)
        .then( response => {
            if (!(response.status === 200)) {
                this.setFieldError('phone_verification', 'That code did not match. Please check the text and try again.');
            } else {
                logEvent('signup: verify contact info', { source: 'signup_flow', step });
                this.logStepComplete(step, 'verify');
                this.setState({ currentStep: step + 1, field_errors: {} });
            };
        });
    }

    submitCredentials(step) {
        if (this.state.open_overlay) {
            return;
        }
        const errors = {};
        if (!this.state.username) {
            errors.username = 'Please choose a username.';
        }
        if (this.state.user_password.length < 8) {
            errors.user_password = 'Please enter a password with at least 8 characters.';
        }
        if (!(this.state.user_password === this.state.user_password_verification)) {
            errors.user_password_verification = 'Both passwords need to match.';
        }
        if (Object.keys(errors).length) {
            this.setState({ field_errors: errors });
            return;
        }

        // re-check uniqueness of username
        const username = this.state.username.toLowerCase();
        GGApi.uniqueUser(username).then(usernameResult => {
            if (usernameResult.status !== 'ok') {
                this.setState({ field_errors: { username: 'Something went wrong. Please try again shortly.' } });
                return;
            }
            this.setState({ username_unique: usernameResult.unique });
            if (!usernameResult.unique) {
                this.setState({ field_errors: { username: 'This username is unavailable. Please try a different one.' } });
                return;
            }

            // use the gg api here to send the new user info to the database
            let newUser = {
                first_name: this.state.first_name,
                last_name: this.state.last_name,
                user_email: this.state.user_email.toLowerCase(),
                user_phone_number: this.state.user_phone_number,
                username: username,
                user_password: this.state.user_password
            }
            // need to add a Captcha here!
            // the following creates a GG user, and also a stripe account and customer.
            this.setState({ field_errors: {} });
            GGApi.create_user(newUser).then(response => {
                if (!response || response.failed) {
                    this.setFieldError('username', 'Something went wrong creating your account. Please try again.');
                    return;
                }
                GGApi.create_stripe_account();
                GGApi.create_stripe_customer();
                logEvent('signup: finish signup', { source: 'signup_flow', step });
                this.logStepComplete(step, 'credentials');
                this.setState({ open_overlay: true });
            });
        });
    }

    handleButtonClick() {
        // scroll to top of page
        window.scrollTo(0, 0);

        const step = this.state.currentStep;
        const screen = this.getScreenForStep(step);

        if (screen === 'name') {
            this.submitName(step);
        } else if (screen === 'contact') {
            this.submitContact(step);
        } else if (screen === 'verify') {
            this.submitVerify(step);
        } else if (screen === 'credentials') {
            this.submitCredentials(step);
        }
    }


    handleBackButton() {
        let step = this.state.currentStep;
        if(this.state.open_overlay){
            return;
        }
        // amplitude
        logEvent('signup: click back', { source: 'signup_flow', step });

        if (step === 1) {
            this.props.history.goBack();
            return;
        }
        this.setState({ currentStep: step - 1, field_errors: {} });
    }

    handleChange(event) {
        const { name, value} = event.target;
        const extra = {};
        if (name === 'username') {
            extra.username_unique = '';
        }
        this.setState({[name]: value, ...extra});
        this.clearFieldError(name);
    }

    handleChangeEmail(event) {
        let { name, value} = event.target;
        value = value.toLowerCase();
        this.setState({[name]: value, email_taken: false, email_unique: ''});
        this.clearFieldError(name);
    }
    
    checkUnique(event, type) {
        if (type === 'email') {
            const value = event.target.value.toLowerCase();
            if (!value) return;
            GGApi.uniqueEmail(value).then(result => {
                if (result.status !== 'ok') {
                    return;
                }
                this.setState({email_unique: result.unique, email_taken: !result.unique});
                if (!result.unique) {
                    this.setFieldError('user_email', 'This email already has a Greatest Gift account.');
                } else {
                    this.clearFieldError('user_email');
                }
            });
        } else if (type === 'phone') {
            if(event.target.value.length === 12) {
                GGApi.uniquePhone(event.target.value).then(result => {
                    if (result.status !== 'ok') {
                        return;
                    }
                    this.setState({phone_unique: result.unique});
                    if (!result.unique) {
                        this.setFieldError('user_phone_number', 'This phone number already has a Greatest Gift account.');
                    } else {
                        this.clearFieldError('user_phone_number');
                    }
                });
            }
            
        } else if (type === 'username') {
            GGApi.uniqueUser(event.target.value).then(result => {
                if (result.status !== 'ok') {
                    return;
                }
                this.setState({username_unique: result.unique});
                if (!result.unique) {
                    this.setFieldError('username', 'This username is unavailable. Please try a different one.');
                } else {
                    this.clearFieldError('username');
                }
            });
        };
    }

    handlePhoneChange(event) {
        const { name, value} = event.target;
        this.setState({[name]: value, phone_support: value, phone_unique: ''});
        this.clearFieldError(name);

        if (event.target.value.length === 3 && this.state.phone_support.length === 2) {
            let newValue = value + "-";
            this.setState({[name]: newValue});
        } else if (event.target.value.length === 7 && this.state.phone_support.length === 6) {
            let newValue = value + "-";
            this.setState({[name]: newValue});
        };
        
    } 

    componentDidMount() {
        this.props.removeNavBar();
        const urlParams = new URLSearchParams(window.location.search);
        const returnTo = urlParams.get('return_to');
        if (returnTo) {
            sessionStorage.setItem('return_after_auth', returnTo);
            this.setState({ next_page: returnTo.split('?')[0], next_page_params: returnTo.includes('?') ? '?' + returnTo.split('?')[1] : '' });
        } else if(urlParams.get('source')){
            // Carry every param except `source` through to the page we return to.
            const forwarded = new URLSearchParams(window.location.search);
            forwarded.delete('source');
            const forwardedQs = forwarded.toString();
            this.setState(
                {   next_page: '/'+urlParams.get('source'),
                    next_page_params: forwardedQs ? '?' + forwardedQs : ''
                })
        }

        const signupSource = urlParams.get('source') || 'direct';
        this.setState({
            value_props: getSignupValueProps({
                source: urlParams.get('source'),
                inviter: urlParams.get('from'),
                childName: urlParams.get('Gift_for'),
            }),
        });

        syncExperimentUserProperties();
        logEvent('signup: start signup', {
            source: signupSource,
            flow_variant: this.getFlowVariant(),
            entry_screen: this.getScreenForStep(1),
        });
    }

    componentWillUnmount() {
        this.props.restoreNavBar();
    }
    
    overlayButton1Handler(){
        console.log('overlay button click.')
    }

    /** One-line value hint above the first screen's fields. */
    renderValueProps() {
        if (this.state.currentStep !== 1) {
            return null;
        }
        const line = this.state.value_props && this.state.value_props.line;
        if (!line) {
            return null;
        }
        return (
            <p className='small_text signup_value_line'>{line}</p>
        );
    }

    render () {
        const buttonText = this.getStepButtonText();

        return (
       <div>
        <Header_for_multistep 
            text='Create An Account'
            handleBackButton={() => this.handleBackButton()}
            hidePersonIcon={true}
        />
       <main>
    <Helmet>
                <title>Sign Up | Greatest Gift</title>
                <meta property="og:title" content='Sign Up | Greatest Gift'/>
                <link rel="icon" href="/static/media/logo_var_2.3a7e8fdf.png" />
                <link rel="canonical" href="https://app.greatestgiftapp.com/new_account"/>
                <meta name="description"
                    content='Sign up for your Greatest Gift account. Send and receive monetary gifts for children`s long term savings.' 
                />
                <meta property="og:description" 
                    content='Sign up for your Greatest Gift account. Send and receive monetary gifts for children`s long term savings.' 
                />
    </Helmet>

            <Tracker    numOfSteps={4} 
                        currentStep={this.state.currentStep}
                        tags={this.getTrackerTags()}
                        />

            <Account_step_1 currentStep={this.state.currentStep} 
                            showAtStep={this.getStepForScreen('name')}
                            handleButtonClick={this.handleButtonClick} 
                            handleChange={this.handleChange}
                            buttonText={buttonText}
                            first_name={this.state.first_name}
                            last_name={this.state.last_name}
                            field_errors={this.state.field_errors}
                            />

            <Account_step_2 currentStep={this.state.currentStep} 
                            showAtStep={this.getStepForScreen('contact')}
                            handleButtonClick={this.handleButtonClick} 
                            handleChange={this.handleChange}
                            buttonText={buttonText}

                            handleChangeEmail={this.handleChangeEmail}
                            checkUnique={this.checkUnique}
                            handlePhoneChange={this.handlePhoneChange}
                            user_email={this.state.user_email}
                            user_phone_number={this.state.user_phone_number}
                            field_errors={this.state.field_errors}
                            email_taken={this.state.email_taken}
                            login_return_to={this.state.next_page !== '/greatest_gift_intro'
                                ? this.state.next_page + (this.state.next_page_params || '')
                                : ''}
                            />

            <Account_step_3 currentStep={this.state.currentStep} 
                            showAtStep={this.getStepForScreen('verify')}
                            handleButtonClick={this.handleButtonClick} 
                            handleChange={this.handleChange}
                            buttonText={buttonText}

                            phone_verification={this.state.phone_verification}
                            field_errors={this.state.field_errors}
                            />

            <Account_step_4 currentStep={this.state.currentStep} 
                            showAtStep={this.getStepForScreen('credentials')}
                            handleButtonClick={this.handleButtonClick} 
                            handleChange={this.handleChange}
                            checkUnique={this.checkUnique}
                            buttonText={buttonText}
                            username={this.state.username}
                            user_password={this.state.user_password}
                            user_password_verification={this.state.user_password_verification}
                            field_errors={this.state.field_errors}
                            />

            {this.getScreenForStep(this.state.currentStep) === 'credentials' ? 
                <div className='terms_of_service_container'>
                    <p className='tiny_text'>By registering your account, you agree to our 
                        <a className='secondary_dark'
                            href='https://app.GreatestGiftApp.com/legal'
                            target='_blank' rel='noreferrer'> Services Agreement
                        </a> and the   
                        <a className='secondary_dark' 
                            href='https://stripe.com/connect-account/legal/full'
                            target='_blank' rel='noreferrer'> Stripe Connected Account Agreement.
                        </a>
                    </p>
                </div> 
                : null
            }

            <Overlay_popup 
                open            = {this.state.open_overlay}
                closer          = {false}
                overlay_title   = 'Congratulations!'
                wide            = {false}
        
                body_1          = "You've joined a community of Great Gifters!"
                
                image           = {cover_picture}
                button_1        = {{    text: (this.state.next_page === '/give-a-gift' || this.state.next_page === '/monthly-matching') ? (this.state.next_page === '/monthly-matching' ? 'Continue to payment' : 'Back to gift') : 'next',
                                        links: true,
                                        linkTo: (this.state.next_page || '/home') + (this.state.next_page_params || ''),
                                        handler: this.overlayButton1Handler
                                    }}    
                joker           = {
                                    <div>
                                        <Link to='/home'>
                                            <p className='secondary_dark'>Skip</p>
                                        </Link>
                                    </div>
                                    }
            />

            <Account_step_5 currentStep={this.state.currentStep} 
                            showAtStep={5}
                            handleButtonClick={this.handleButtonClick} 
                            buttonText={buttonText}
                            />

            {this.renderValueProps()}

        
        </main>
        
           <div className='buffer'></div>

        <MasterButton 
            buttonText={buttonText} 
            handleButtonClick={this.handleButtonClick}
            links={this.state.buttonLinks}
            linkTo='/home'
        />
        </div>
        );
    }
}

export default Main_account_creation;
